/**
 * Resolve experience for ONE finding, by resolvability (spec §1 approve-first, §2 resolvability):
 * - auto      -> Resolve -> preview (before/after diff, risk, reversible, provider, plain words) -> Approve (MFA step-up
 *                when asked) / Reject -> live status -> Rollback (approve-first).
 * - connect   -> auto finding whose provider is not connected: "Connect <provider> to fix this automatically".
 * - assisted  -> generated content/code with copy buttons -> "I've done it" -> re-test.
 * - manual    -> steps checklist -> "Mark done" -> re-test.
 * - expert    -> "Ask a Quixo expert" (T10 booking route).
 *
 * T4 slot contract: render `<ResolveButton finding={finding} jobId={jobId} />` in the finding row/card; it owns the
 * disclosure and renders <ResolvePanel> inline. Use <ResolvePanel> directly for a drawer/side panel.
 */
import { useEffect, useId, useMemo, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router';
import type { AdvancedFinding, ProviderConnection, RemediationProposal } from '@qm/contracts/audit-remediation';
import type { LocalizedText } from '@qm/contracts/audit-analysis';
import { Badge, Button, ErrorState, Loading } from '@qm/ui';
import { useApp } from '../../../../../lib/app-context';
import { useFormat } from '../../../../../lib/format';
import { DiffViewer } from './DiffViewer';
import { StepUpPrompt } from './StepUpPrompt';
import { FailureState, useLoad, useRemediationClient } from './context';
import {
  canRollback, connectionFor, createGuideFlow, createResolveFlow, LIVE_STEPS, liveStatus, resolveKind, stepStates,
  type GuideState, type ResolveKind, type ResolveState,
} from './resolve-flow';
import './actions.css';

export interface ResolveProps {
  finding: AdvancedFinding;
  jobId: string;
  /** Provide when the host already loaded connections (saves one request per finding). */
  connections?: readonly ProviderConnection[];
}

export const lt = (x: LocalizedText, locale: string) => (locale === 'ar' && x.ar ? x.ar : x.en);
export const riskTone = (r: string) => (r === 'low' ? 'success' : r === 'medium' ? 'warning' : 'error') as 'success' | 'warning' | 'error';
/** Expert call booking: the advanced report opens this finding with T10's booking dialog (report/slots.tsx). */
export const expertHref = (jobId: string, findingId: string) => `/audit/${encodeURIComponent(jobId)}/advanced?finding=${encodeURIComponent(findingId)}&expert=1`;
export const connectHref = (jobId: string, provider: string) => `/audit/advanced/integrations?job=${encodeURIComponent(jobId)}#provider-${provider}`;

/** Inline trigger for the report's ResolveSlot (T4). Owns the disclosure; expert/connect render as links. */
export function ResolveButton({ finding, jobId, connections }: ResolveProps) {
  const { t } = useApp();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const loaded = useLoad(connections ? null : 'connections', (c) => c.connections());
  const cons = connections ?? (loaded.status === 'ready' ? loaded.data : []);
  const kind = resolveKind(finding, cons);
  const provider = finding.resolvability.provider;
  if (kind === 'expert') return <Link className="cu-btn" data-variant="outline" to={expertHref(jobId, finding.id)}>{t('audit-remediation.actions.expert.cta')}</Link>;
  if (kind === 'connect' && provider) return <Link className="cu-btn" data-variant="outline" to={connectHref(jobId, provider)}>{t('audit-remediation.actions.connect.cta', { provider: t(`audit-remediation.provider.${provider}`) })}</Link>;
  const inFlight = ['awaiting_approval', 'applying', 'applied', 'verified', 'failed'].includes(finding.status);
  const label = kind === 'auto' ? (inFlight ? 'audit-remediation.actions.resolve.review' : 'audit-remediation.actions.resolve.cta')
    : kind === 'assisted' ? 'audit-remediation.actions.assisted.cta' : 'audit-remediation.actions.manual.cta';
  return (
    <div className="ar-resolve">
      <Button variant={open ? 'secondary' : 'accent'} aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((v) => !v)} data-testid="resolve-button">
        {t(open ? 'audit-remediation.actions.resolve.hide' : label)}
      </Button>
      <div id={panelId} hidden={!open}>{open ? <ResolvePanel finding={finding} jobId={jobId} connections={cons} /> : null}</div>
    </div>
  );
}

/** The resolve experience itself (inline or in a drawer). */
export function ResolvePanel({ finding, jobId, connections }: ResolveProps) {
  const loaded = useLoad(connections ? null : 'connections', (c) => c.connections());
  if (!connections && loaded.status !== 'ready') {
    return loaded.status === 'loading' ? <PanelLoading /> : <FailureState failure={loaded.failure} />;
  }
  const cons = connections ?? (loaded.status === 'ready' ? loaded.data : []);
  return <ResolveBody finding={finding} jobId={jobId} connections={cons} kind={resolveKind(finding, cons)} />;
}

function PanelLoading() { const { t } = useApp(); return <Loading label={t('common.loading')} />; }

function ResolveBody({ finding, jobId, connections, kind }: Required<ResolveProps> & { kind: ResolveKind }) {
  if (kind === 'auto') return <AutoPanel finding={finding} jobId={jobId} connections={connections} />;
  if (kind === 'assisted' || kind === 'manual') return <GuidePanel finding={finding} jobId={jobId} connections={connections} />;
  return <div className="ar-panel"><ConnectOrExpert kind={kind} finding={finding} jobId={jobId} /></div>;
}

export function ConnectOrExpert({ kind, finding, jobId }: { kind: 'connect' | 'expert'; finding: AdvancedFinding; jobId: string }) {
  const { t } = useApp();
  const provider = finding.resolvability.provider;
  if (kind === 'connect' && provider) {
    const name = t(`audit-remediation.provider.${provider}`);
    return (
      <div className="ar-callout" data-testid="not-connected">
        <p>{t('audit-remediation.actions.connect.body', { provider: name })}</p>
        <Link className="cu-btn" to={connectHref(jobId, provider)}>{t('audit-remediation.actions.connect.cta', { provider: name })}</Link>
      </div>
    );
  }
  return (
    <div className="ar-callout" data-testid="expert">
      <p>{t('audit-remediation.resolvabilityHint.expert')}</p>
      <Link className="cu-btn" to={expertHref(jobId, finding.id)}>{t('audit-remediation.actions.expert.cta')}</Link>
    </div>
  );
}

// ---------- auto ----------
function AutoPanel({ finding, jobId, connections }: Required<ResolveProps>) {
  const client = useRemediationClient();
  const flow = useMemo(() => createResolveFlow({ client, finding, jobId }), [client, finding, jobId]);
  const state = useSyncExternalStore(flow.subscribe, flow.getState, flow.getState);
  useEffect(() => { void flow.start(connections); return () => flow.dispose(); }, [flow, connections]);
  return (
    <AutoView finding={finding} state={state} connection={connectionFor(finding.resolvability.provider, connections)} on={{
      approve: () => void flow.approve(), reject: (n) => void flow.reject(n), stepUp: (c) => void flow.submitStepUp(c), cancelStepUp: flow.cancelStepUp,
      askRollback: flow.askRollback, cancelRollback: flow.cancelRollback, confirmRollback: () => void flow.confirmRollback(), retry: () => void flow.retry(connections),
    }} />
  );
}

export interface AutoHandlers {
  approve(): void; reject(note?: string): void; stepUp(code: string): void; cancelStepUp(): void;
  askRollback(): void; cancelRollback(): void; confirmRollback(): void; retry(): void;
}

/** Pure view of the auto flow (rendered in tests for every phase). */
export function AutoView({ finding, state, connection, on }: { finding: AdvancedFinding; state: ResolveState; connection: ProviderConnection | null; on: AutoHandlers }) {
  const { t, locale } = useApp();
  const f = useFormat();
  const headId = useId();
  const [note, setNote] = useState('');
  const q = state.proposal;
  return (
    <section className="ar-panel" aria-labelledby={headId} data-phase={state.phase} data-testid="resolve-panel">
      <p className="ar-panel__title" id={headId}>{t('audit-remediation.actions.resolve.title')}: <bdi>{f.text(lt(finding.title, locale))}</bdi></p>
      {state.phase === 'idle' || state.phase === 'loading' ? <Loading label={t('audit-remediation.actions.resolve.preparing')} /> : null}
      {state.phase === 'error' ? (
        <>
          <ErrorState message={t(state.error ?? 'audit-remediation.actions.errors.generic')} />
          <div className="cu-btnrow"><Button onClick={on.retry}>{t('audit-remediation.actions.retry')}</Button></div>
        </>
      ) : null}
      {q && (state.phase === 'preview' || (state.phase === 'step_up' && state.pending === 'approve')) ? (
        <>
          <ProposalFacts proposal={q} connection={connection} finding={finding} />
          <DiffViewer before={q.before} after={q.after} diff={q.diff} />
          {state.phase === 'preview' ? (
            <div className="ar-decide">
              <div className="cu-field">
                <label htmlFor={`${headId}-note`}>{t('audit-remediation.actions.resolve.note')}</label>
                <input id={`${headId}-note`} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
              </div>
              <div className="cu-btnrow">
                <Button variant="accent" disabled={state.busy} onClick={on.approve} data-testid="approve">{t('audit-remediation.actions.resolve.approve')}</Button>
                <Button disabled={state.busy} onClick={() => on.reject(note)} data-testid="reject">{t('audit-remediation.actions.resolve.reject')}</Button>
              </div>
              <p className="ar-muted">{t('audit-remediation.actions.resolve.approveHint')}</p>
            </div>
          ) : null}
        </>
      ) : null}
      {q && state.phase === 'rollback_confirm' ? (
        <div className="ar-rollback" data-testid="rollback-confirm">
          <p>{t('audit-remediation.actions.rollback.confirmBody', { provider: t(`audit-remediation.provider.${q.provider}`) })}</p>
          <DiffViewer before={q.before} after={q.after} diff={q.diff} inverted />
          <div className="cu-btnrow">
            <Button variant="accent" disabled={state.busy} onClick={on.confirmRollback} data-testid="confirm-rollback">{t('audit-remediation.actions.rollback.confirm')}</Button>
            <Button disabled={state.busy} onClick={on.cancelRollback}>{t('audit-remediation.actions.cancel')}</Button>
          </div>
        </div>
      ) : null}
      {state.phase === 'step_up' && state.pending ? (
        <StepUpPrompt action={state.pending} busy={state.busy} error={state.stepUpError} onSubmit={on.stepUp} onCancel={on.cancelStepUp} />
      ) : null}
      {q && (state.phase === 'tracking' || state.phase === 'done') ? <LiveStatusView proposal={q} stale={state.stale} rollingBack={state.rollingBack} onRollback={on.askRollback} onRetry={on.retry} busy={state.busy} /> : null}
      {state.error && state.phase !== 'error' ? <ErrorState message={t(state.error)} /> : null}
    </section>
  );
}

function ProposalFacts({ proposal: q, connection, finding }: { proposal: RemediationProposal; connection: ProviderConnection | null; finding: AdvancedFinding }) {
  const { t, locale } = useApp();
  const f = useFormat();
  const provider = t(`audit-remediation.provider.${q.provider}`);
  const target = q.target.label ?? q.target.ref;
  return (
    <>
      <p className="ar-plain" data-testid="plain-words">
        {t('audit-remediation.actions.preview.plain', { provider, target: f.text(target) })} <bdi>{f.text(lt(finding.fix, locale))}</bdi>
      </p>
      <ul className="qs-kv ar-facts">
        <li><span className="qs-kv__label">{t('audit-remediation.actions.preview.provider')}</span><span className="qs-kv__value">{provider}{connection ? <> · <bdi>{f.text(connection.accountLabel)}</bdi></> : null}</span></li>
        <li><span className="qs-kv__label">{t('audit-remediation.actions.preview.target')}</span><span className="qs-kv__value"><bdi>{f.text(target)}</bdi></span></li>
        <li><span className="qs-kv__label">{t('audit-remediation.actions.preview.risk')}</span><span className="qs-kv__value"><Badge tone={riskTone(q.risk)}>{t(`audit-remediation.risk.${q.risk}`)}</Badge></span></li>
        <li><span className="qs-kv__label">{t('audit-remediation.actions.preview.reversible')}</span><span className="qs-kv__value">{t(q.reversible ? 'audit-remediation.actions.preview.reversibleYes' : 'audit-remediation.actions.preview.reversibleNo')}</span></li>
        <li><span className="qs-kv__label">{t('audit-remediation.actions.preview.expires')}</span><span className="qs-kv__value">{f.dateTime(q.expiresAt)}</span></li>
      </ul>
    </>
  );
}

export function LiveStatusView({ proposal: q, stale, rollingBack = false, onRollback, onRetry, busy }: { proposal: RemediationProposal; stale: boolean; rollingBack?: boolean; onRollback: () => void; onRetry: () => void; busy: boolean }) {
  const { t } = useApp();
  const live = rollingBack ? 'rollingBack' : liveStatus(q);
  const steps = stepStates(q);
  const inFlight = ['approved', 'applying', 'applied'].includes(q.status);
  return (
    <div className="ar-live" data-testid="live-status" data-status={q.status}>
      <p role="status" aria-live="polite" className="ar-live__now"><Badge tone={live === 'verified' ? 'success' : live === 'failed' ? 'error' : 'accent'}>{t(`audit-remediation.actions.live.${live}`)}</Badge></p>
      {!rollingBack && (inFlight || q.status === 'verified' || q.status === 'failed') ? (
        <ol className="ar-steps" aria-label={t('audit-remediation.actions.live.progress')}>
          {LIVE_STEPS.map((s) => <li key={s} data-state={steps[s]}><span>{t(`audit-remediation.actions.live.step.${s}`)}</span><span className="qm-sr-only"> ({t(`audit-remediation.actions.live.state.${steps[s]}`)})</span></li>)}
        </ol>
      ) : null}
      <p>{t(`audit-remediation.actions.live.body.${live}`)}</p>
      {q.status === 'failed' && q.failureCode ? <p className="ar-muted">{t('audit-remediation.actions.live.code', { code: q.failureCode })}</p> : null}
      {stale ? <p className="ar-muted">{t('audit-remediation.actions.live.stale')}</p> : null}
      <div className="cu-btnrow">
        {canRollback(q) && !rollingBack ? <Button disabled={busy} onClick={onRollback} data-testid="rollback">{t('audit-remediation.actions.rollback.cta')}</Button> : null}
        {!rollingBack && ['rejected', 'expired', 'rolled_back', 'failed'].includes(q.status) ? <Button disabled={busy} onClick={onRetry}>{t('audit-remediation.actions.resolve.again')}</Button> : null}
      </div>
    </div>
  );
}

// ---------- assisted + manual ----------
function GuidePanel({ finding, jobId, connections }: Required<ResolveProps>) {
  const client = useRemediationClient();
  const flow = useMemo(() => createGuideFlow({ client, finding, jobId }), [client, finding, jobId]);
  const state = useSyncExternalStore(flow.subscribe, flow.getState, flow.getState);
  useEffect(() => { void flow.start(); }, [flow]);
  const provider = finding.resolvability.provider;
  const linked = connectionFor(provider, connections)?.status === 'connected';
  return <GuideView finding={finding} jobId={jobId} state={state} canFinish={flow.canFinish()} connectHint={provider && !linked ? provider : null}
    on={{ toggle: flow.toggle, done: () => void flow.done(), retry: () => void flow.start() }} />;
}

export function GuideView({ finding, jobId, state, canFinish, connectHint, on }: {
  finding: AdvancedFinding; jobId: string; state: GuideState; canFinish: boolean; connectHint: string | null;
  on: { toggle(i: number): void; done(): void; retry(): void };
}) {
  const { t, locale } = useApp();
  const f = useFormat();
  const headId = useId();
  const [copied, setCopied] = useState<string | null>(null);
  const mode = finding.resolvability.mode;
  const copy = (id: string, text: string) => {
    void navigator.clipboard?.writeText(text).then(() => setCopied(id), () => setCopied(null));
  };
  return (
    <section className="ar-panel" aria-labelledby={headId} data-testid={`guide-${mode}`}>
      <p className="ar-panel__title" id={headId}>{t(`audit-remediation.actions.${mode === 'assisted' ? 'assisted' : 'manual'}.title`)}: <bdi>{f.text(lt(finding.title, locale))}</bdi></p>
      {connectHint ? (
        <p className="ar-muted">{t('audit-remediation.resolvabilityHint.connect', { provider: t(`audit-remediation.provider.${connectHint}`) })}{' '}
          <Link to={connectHref(jobId, connectHint)}>{t('audit-remediation.actions.connect.short')}</Link></p>
      ) : null}
      {state.phase === 'loading' || state.phase === 'idle' ? <Loading label={t('audit-remediation.actions.assisted.generating')} /> : null}
      {state.phase === 'error' ? <><ErrorState message={t(state.error ?? 'audit-remediation.actions.errors.generic')} /><Button onClick={on.retry}>{t('audit-remediation.actions.retry')}</Button></> : null}
      {state.phase === 'ready' && mode === 'assisted' && state.assist ? (
        <ul className="ar-blocks">
          {state.assist.blocks.map((b) => (
            <li key={b.id} className="ar-block">
              <div className="ar-block__head">
                <p className="ar-block__label"><bdi>{f.text(b.label)}</bdi></p>
                <Button size="sm" onClick={() => copy(b.id, b.content)} aria-label={t('audit-remediation.actions.assisted.copyLabel', { label: f.text(b.label) })}>
                  {t(copied === b.id ? 'audit-remediation.actions.assisted.copied' : 'audit-remediation.actions.assisted.copy')}
                </Button>
              </div>
              {b.where ? <p className="ar-muted"><bdi>{f.text(b.where)}</bdi></p> : null}
              <pre dir="ltr" className="ar-code"><code>{b.content}</code></pre>
            </li>
          ))}
        </ul>
      ) : null}
      {state.phase === 'ready' && mode === 'manual' ? (
        <fieldset className="ar-checklist">
          <legend>{t('audit-remediation.actions.manual.steps')}</legend>
          {finding.steps.map((s, i) => (
            <label key={i} className="ar-check">
              <input type="checkbox" checked={state.checked.includes(i)} onChange={() => on.toggle(i)} />
              <span><bdi>{f.text(lt(s, locale))}</bdi></span>
            </label>
          ))}
        </fieldset>
      ) : null}
      {state.phase === 'ready' ? (
        <div className="cu-btnrow">
          <Button variant="accent" disabled={state.busy || !canFinish} onClick={on.done} data-testid="guide-done">
            {t(mode === 'assisted' ? 'audit-remediation.actions.assisted.done' : 'audit-remediation.actions.manual.done')}
          </Button>
        </div>
      ) : null}
      <p role="status" aria-live="polite" className="ar-retest">
        {copied ? <span className="qm-sr-only">{t('audit-remediation.actions.assisted.copied')}</span> : null}
        {state.busy ? t('audit-remediation.actions.retest.running') : state.retest ? t(`audit-remediation.actions.retest.${state.retest.status}`) : ''}
      </p>
      {state.retest?.reason ? <p className="ar-muted"><bdi>{f.text(state.retest.reason)}</bdi></p> : null}
      {state.error && state.phase === 'ready' ? <ErrorState message={t(state.error)} /> : null}
    </section>
  );
}
